import React from "react";
import {
  TextField as MuiTextField,
  Select as MuiSelect,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  InputAdornment,
} from "@mui/material";
import { useDesignTokens } from "../../../theme/useDesignTokens";
import { ACTION_BUTTON_HEIGHT, ACTION_BUTTON_MIN_WIDTH } from "../../../constants/uiConstants";

/**
 * Common Input Component
 * Provides consistent form field styling, validation, and error handling
 * Supports TextField, Select, Textarea, and custom types
 */
const FormInput = ({
  id,
  type = "text",
  label,
  name,
  value = "",
  onChange,
  onBlur,
  error = false,
  helperText = "",
  placeholder = "",
  fullWidth = true,
  size = "small",
  disabled = false,
  required = false,
  multiline = false,
  rows = 1,
  slotProps = {},
  onKeyDown,
  options = [], // For select
  renderOption,
  sx = {},
  touched = "false",
  startAdornment = null,
  endAdornment = null,
  // showError = touched && error,
  accept = "", // New prop for file types
  multiple = false, // New prop for multiple files
  inputRef = null, // Set to null by default so it's strictly optional
  ...props
}) => {
  const { colors } = useDesignTokens();
  const showError = (touched === "true" ? true : false) && error;
  const selectId = id || name;
  const selectLabelId = label ? `${selectId}-label` : undefined;
  const shouldShrinkSelectLabel = Boolean(label) && Boolean(props.displayEmpty);
  // Multi-select handling is kept in this shared input so country and similar dropdowns
  // can use a single component without custom wrappers.
  const isSelectMultiple = type === "select" && Boolean(multiple);
  const [isMultiSelectOpen, setIsMultiSelectOpen] = React.useState(false);
  // Guard against stale values: if API/default value is not present in options,
  // fallback to empty so MUI Select does not show an invalid selection.
  const optionValues = options.map((option) => option?.value ?? "");
  const normalizedSelectValue = isSelectMultiple
    ? (Array.isArray(value) ? value : []).filter((selectedValue) =>
        optionValues.includes(selectedValue)
      )
    : optionValues.includes(value) || value === ""
    ? value
    : "";

  const handleKeyDown = (e) => {
    onKeyDown?.(e);
  };

  const blurActiveElement = () => {
    if (typeof document !== "undefined" && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  const mergedSlotProps = {
    ...slotProps,

    /** Native input element */
    htmlInput: {
      ...(slotProps?.htmlInput || {}),
      // Add file-specific attributes
      ...(type === "file" && { accept, multiple }),
      className: ["browser-default", slotProps?.htmlInput?.className]
        .filter(Boolean)
        .join(" "),
    },

    /** Target the Label (Replaces deprecated InputLabelProps) */
    inputLabel: {
      ...(slotProps?.inputLabel || {}),
      // Ensure file inputs always have a shrunk label
      shrink: type === "file" ? true : undefined,
    },

    /** MUI InputBase (adornments live here) */
    input: {
      ...(slotProps?.input || {}),

      ...(startAdornment && {
        startAdornment: (
          <InputAdornment position="start">{startAdornment}</InputAdornment>
        ),
      }),

      ...(endAdornment && {
        endAdornment: (
          <InputAdornment position="end">{endAdornment}</InputAdornment>
        ),
      }),
    },
  };

  const primaryColor = colors?.primary?.main || "#ED6C02";
  const controlHeight = multiline ? "auto" : `${ACTION_BUTTON_HEIGHT}px`;
  const commonSx = {
    "& .MuiOutlinedInput-root": {
      height: controlHeight === "auto" ? "auto" : ACTION_BUTTON_HEIGHT,
      minHeight: controlHeight === "auto" ? undefined : ACTION_BUTTON_HEIGHT,
      boxSizing: "border-box",
      ...(type === "file" && { display: "flex", alignItems: "center" }),
    },
    "& .MuiInputBase-input": {
      boxSizing: "border-box !important",
      borderBottom: "none !important", // Explicitly kill Materialize border
      boxShadow: "none !important", // Explicitly kill Materialize shadow
      margin: "0 !important",
      height: multiline
        ? "auto !important"
        : `${ACTION_BUTTON_HEIGHT}px !important`,
      padding:
        size === "small" ? "8.5px 14px !important" : "16.5px 14px !important",
      ...(type === "file" && {
        lineHeight: `${ACTION_BUTTON_HEIGHT}px`,
        paddingTop: "0px !important",
        cursor: "pointer",
      }),
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "1px solid rgba(0, 0, 0, 0.42) !important",
    },
    "& .MuiOutlinedInput-notchedOutline legend": {
      display: "block !important",
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: primaryColor + " !important",
      borderWidth: "1px !important",
    },
    "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: primaryColor + " !important",
      borderWidth: "1px !important",
    },
    /* Expanded/open select should keep the same focus highlight */
    "& .Mui-expanded .MuiOutlinedInput-notchedOutline": {
      borderColor: primaryColor + " !important",
      borderWidth: "1px !important",
    },
    "& .Mui-error .MuiOutlinedInput-notchedOutline": {
      borderColor: colors?.error?.main + " !important",
      borderWidth: "1px !important",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: primaryColor + " !important",
    },
    "& .MuiInputLabel-root.Mui-expanded": {
      color: primaryColor + " !important",
    },
    "& .MuiInputLabel-root.Mui-error": {
      color: colors?.error?.main + " !important",
    },
    minWidth: `${ACTION_BUTTON_MIN_WIDTH}px`,
    ...sx, // Allow parent overrides
  };

  // TextField and Textarea
  if (
    type === "text" ||
    type === "number" ||
    type === "email" ||
    type === "password" ||
    type === "textarea" ||
    type === "file"
  ) {
    return (
      <MuiTextField
        fullWidth={fullWidth}
        label={label}
        // id={name}
        id={id || name}
        name={name}
        type={type === "textarea" ? "text" : type}
        // value={value}
        // value must be empty for file inputs to avoid security errors
        {...(type !== "file" ? { value } : {})}
        onChange={onChange}
        onBlur={onBlur}
        error={showError}
        helperText={helperText}
        placeholder={placeholder}
        size={size}
        disabled={disabled}
        required={required}
        multiline={type === "textarea" || multiline}
        rows={type === "textarea" ? rows : multiline ? rows : undefined}
        onKeyDown={onKeyDown ? handleKeyDown : undefined}
        slotProps={mergedSlotProps}
        sx={commonSx}
        inputRef={inputRef} // Forwarding the ref
        {...props}
      />
    );
  }

  // Select/Dropdown
  if (type === "select") {
    const {
      MenuProps: selectMenuProps,
      onOpen: selectOnOpen,
      onClose: selectOnClose,
      renderValue: selectRenderValue,
      ...selectProps
    } = props;

    return (
      <FormControl
        fullWidth={fullWidth}
        size={size}
        error={showError}
        disabled={disabled}
        sx={commonSx}
      >
        {label && (
          <InputLabel id={selectLabelId} shrink={shouldShrinkSelectLabel || undefined}>
            {label}
          </InputLabel>
        )}
        <MuiSelect
          id={selectId}
          labelId={selectLabelId}
          name={name}
          multiple={isSelectMultiple}
          value={normalizedSelectValue}
          open={isSelectMultiple ? isMultiSelectOpen : undefined}
          onOpen={(...args) => {
            if (isSelectMultiple) {
              setIsMultiSelectOpen(true);
            }
            selectOnOpen?.(...args);
          }}
          onClose={(...args) => {
            if (isSelectMultiple) {
              setIsMultiSelectOpen(false);
            }
            selectOnClose?.(...args);
          }}
          onChange={(e) => {
            if (isSelectMultiple) {
              // MUI may return comma-separated string in some cases; normalize to array
              // so consumers always receive consistent multi-select payload.
              const nextValue = e.target.value;
              const normalizedValue = typeof nextValue === "string" ? nextValue.split(",") : nextValue;
              onChange?.({
                ...e,
                target: {
                  ...e.target,
                  name,
                  value: normalizedValue,
                },
              });
            } else {
              onChange?.(e);
              // Keep previous single-select behavior: release retained focus after selection.
              setTimeout(blurActiveElement, 0);
            }
          }}
          onBlur={onBlur}
          onKeyDown={onKeyDown ? handleKeyDown : undefined}
          label={label}
          renderValue={selectRenderValue}
          disabled={disabled}
          MenuProps={{
            ...(slotProps?.select?.MenuProps || selectMenuProps || {}),
          }}
          inputRef={inputRef} // Forwarding the ref
          slotProps={{
            input: {
              className: "browser-default",
            },
          }}
          {...selectProps}
        >
          {options.map((option, index) =>
            renderOption ? (
              // Allows callers to customize option UI (used by country/device ownership lists).
              renderOption(option, index)
            ) : (
              <MenuItem
                key={`${option?.value ?? option?.label ?? "option"}-${index}`}
                value={option?.value ?? ""}
              >
                {option?.label ?? ""}
              </MenuItem>
            )
          )}
        </MuiSelect>
        {(showError || helperText) && (
          <FormHelperText>{showError ? helperText : helperText}</FormHelperText>
        )}
      </FormControl>
    );
  }

  return null;
};

export default FormInput;
